import React from 'react';
import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IAlertsService } from '../../infrastructure/alerts';
import {
    EVENT_TRACKER_FILTER_VIEW_TAB,
    EVENT_TRACKER_TAB,
    IDeliveryItemDetailsDTO,
    IEventsDAO,
    IEventsTrackerListDTO,
    IEventTrackerDetailsDTO,
    IFiltersView,
    IFilterValue,
    ISelectedFilter,
    TGetEventTrackerSortKeys,
} from '../../api/events.dao';
import { PagingViewModel } from '../global/_paging/paging-viewmodels/regular-paging.viewmodel';
import { SortOrder } from '../../api/shared.dto';
import { INPUT_DEBOUNCE_TIME, MINIMAL_SEARCH_LENGTH } from '../../constants/common.constants';
import { IUsersDAO } from '../../api/users.dao';
import { IAccountsDAO } from '../../api/accounts.dao';
import Text from '../global/text/text';
import { debounce } from '../../infrastructure/utils/debounce.utils';
import { DeliveryTeamMemberItemsMapper } from '../../modules/delivery-team-members/delivery-teammember-items.mapper';
import { IEventTrackerGroup } from './_event-tracker-list/_event-tracker-list-group-item/event-tracker-list-group-item';
import { hasPermission } from '../../infrastructure/utils/has-permissions';
import { Permission } from '../../api/roles.dao';
import {format} from 'date-fns';
import {getUserTimeZoneOffsetString} from '../../infrastructure/utils/date.util';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { convertBoolTypeForMixPanel, TFilterName, TRACKING_EVENTS, TSelectedFilterOptions } from '../../tracking-service/tracking-values';
import { buildSelectedFiltersList, getFiltersNames, isEmptyArray } from '../../infrastructure/utils/common.utils';

const UPCOMING_TAB_CONFIGURATION = {
    withGrouping: true,
    withTimeColumn: true,
    withDateRangeColumn: true,
    withTotalPresenterCombinationsByDate: true,
    tabName: EVENT_TRACKER_TAB.UPCOMING,
    sortOrder: SortOrder.Asc,
    sortField: 'mergedDateTime'
};

const PAST_TAB_CONFIGURATION = {
    withDateColumn: true,
    tabName: EVENT_TRACKER_TAB.PAST,
    sortOrder: SortOrder.Desc,
    sortField: 'eventStartDateTime'
};

const DELIVERED_TAB_CONFIGURATION = {
    withDateColumn: true,
    tabName: EVENT_TRACKER_TAB.DELIVERED,
    sortOrder: SortOrder.Desc,
    sortField: 'eventStartDateTime'
};

const EVENT_TRACKER_FILTER_VIEW_TAB_MAPPER = {
    [EVENT_TRACKER_TAB.UPCOMING] : EVENT_TRACKER_FILTER_VIEW_TAB.EVENT_TRACKER_UPCOMING,
    [EVENT_TRACKER_TAB.PAST] : EVENT_TRACKER_FILTER_VIEW_TAB.EVENT_TRACKER_PAST,
    [EVENT_TRACKER_TAB.DELIVERED] : EVENT_TRACKER_FILTER_VIEW_TAB.EVENT_TRACKER_DELIVERED,
};

export const EVENT_TRACKER_TABS_MAPPER = {
    [EVENT_TRACKER_TAB.UPCOMING] : 'Upcoming',
    [EVENT_TRACKER_TAB.PAST] : 'Past',
    [EVENT_TRACKER_TAB.DELIVERED] : 'Delivered',
};

class EventTrackerVM implements IModuleVM {
    @mobx.observable
    public eventTrackerList: IEventTrackerDetailsDTO[];
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public searchQuery: string = '';
    @mobx.observable
    public selectedFilterDeliveryTeam: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterEventLinks: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterAccounts: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterEventTypes: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterEventRegions: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterEventBusinessLines: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterStartDate: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterEndDate: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterColumns: IFilterValue[] = [];
    @mobx.observable
    public selectedFilterCoordinators: IFilterValue[] = [];
    @mobx.observable
    public shouldRedirectToFirstPage = false;
    @mobx.observable
    public collapsedGroups: IEventTrackerGroup[] = [];
    @mobx.observable
    public filterViews: IFiltersView[];

    private subscriptions: Unsubscribe[];
    private paginationVM: PagingViewModel;

    public withGrouping: boolean;
    public withDateColumn: boolean;
    public withTimeColumn: boolean;
    public withDateRangeColumn: boolean;
    public withTotalPresenterCombinationsByDate: boolean;
    public localStorageFiltersName: string;
    public tabName: EVENT_TRACKER_TAB;
    public sortOrder: SortOrder.Asc;
    public sortField: TGetEventTrackerSortKeys;
    public defaultColumns: IFilterValue[] = [];

    constructor(
        public readonly route: Routes,
        public editEvent: (event: IEventTrackerDetailsDTO, composedId: string, intl: IntlShape) => void,
        public onPlanningNotesClick: (accountId: number, eventId: number, composedId: string, intl: IntlShape) => void,
        public isPlanningNotesFormShown: (composedId: string) => boolean,
        @IoC.Inject private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly accountsDAO: IAccountsDAO = IoC.Container.get(IAccountsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        this.paginationVM = new PagingViewModel();
        mobx.reaction(this.paginationVM.getCurrentPage, this.fetchEventsList);
    }

    didMount(): void {
        this.setUpTabConfiguration();
        this.subscriptions = [];
        this.searchEventsCallback = debounce(this.searchEvents, INPUT_DEBOUNCE_TIME);

        this.subscriptions.push(
            this.router.listen(this.route, () => {
                this.loadFiltersFromStorage();
            })
        );
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    private setUpTabConfiguration() {
        let currentTabConfiguration;
        if (this.route === Routes.EventTrackerUpcoming) {
            currentTabConfiguration = UPCOMING_TAB_CONFIGURATION;
        } else if (this.route === Routes.EventTrackerPast) {
            currentTabConfiguration = PAST_TAB_CONFIGURATION;
        } else {
            currentTabConfiguration = DELIVERED_TAB_CONFIGURATION;
        }

        Object.entries(currentTabConfiguration).forEach(([key, value]) => this[key] = value);
        this.localStorageFiltersName = `${EVENT_TRACKER_FILTER_VIEW_TAB_MAPPER[this.tabName]}_FILTERS`;
        this.setDefaultColumns();
    }

    private setDefaultColumns() {
        const viewPlanningNotesAccess = hasPermission(Permission.PLANNING_NOTES);

        this.defaultColumns = [
            this.withTimeColumn && { id: 'displayedTime', label: 'Time' },
            this.withDateColumn && { id: 'dateTime', label: 'Date' },
            { id: 'account', label: 'Account' },
            { id: 'oeNumber', label: 'OE #' },
            { id: 'region', label: 'Region' },
            { id: 'businessLine', label: 'Business Line' },
            { id: 'type', label: 'Type' },
            { id: 'totalPresenters', label: 'Total Presenters' },
            this.withTotalPresenterCombinationsByDate && { id: 'totalPresenterCombinationsByDate', label: 'Daily Presenters' },
            { id: 'coordinators', label: 'Coordinators' },
            { id: 'deliveryTeam', label: 'Delivery Team' },
            { id: 'links', label: 'Links' },
            viewPlanningNotesAccess && { id: 'notes', label: 'Notes' }
        ].filter(Boolean);
        this.selectedFilterColumns = this.defaultColumns;
    }

    public getPagingVM() {
        return this.paginationVM;
    }

    public fetchEventsList = async () => {
        mobx.runInAction(() => {
            this.isLoading = true;
        });
        if (this.shouldRedirectToFirstPage || !this.paginationVM.getCurrentPageForBE()) {
            this.paginationVM.selectPage(1);
            this.shouldRedirectToFirstPage = false;
        }
        try {
            const response = await this.eventsDAO.getAllEvents({
                size: this.paginationVM.getPageSize(),
                page: this.paginationVM.getCurrentPageForBE(),
                query: this.searchQuery.trim(),
                sortField: this.sortField,
                sortOrder: this.sortOrder,
                eventTrackerTab: this.tabName,
                timeZoneOffset: getUserTimeZoneOffsetString(),
                ...this.getFilterParams()
            });
            this.paginationVM.setTotalRecords(response.totalElements);
            this.onFetchEventsListSuccess(response);
        } catch (error) {
            this.onFetchEventsListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    private getFilterParams = () => {
        const getStringifiedIds = (filterArray: IFilterValue[]): string => {
            return filterArray.map(el => el.id).join();
        };

        return {
            ...(this.selectedFilterCoordinators.length && { coordinators: getStringifiedIds(this.selectedFilterCoordinators) }),
            ...(this.selectedFilterDeliveryTeam.length && { deliveryTeamMembers: getStringifiedIds(this.selectedFilterDeliveryTeam) }),
            ...(this.selectedFilterAccounts.length && { accountIds: getStringifiedIds(this.selectedFilterAccounts) }),
            ...(this.selectedFilterEventTypes.length && { eventTypes: getStringifiedIds(this.selectedFilterEventTypes) }),
            ...(this.selectedFilterEventLinks.length && { eventLinks: getStringifiedIds(this.selectedFilterEventLinks) }),
            ...(this.selectedFilterEventRegions.length && { eventRegions: getStringifiedIds(this.selectedFilterEventRegions) }),
            ...(this.selectedFilterEventBusinessLines.length && { eventBusinessLines: getStringifiedIds(this.selectedFilterEventBusinessLines) }),
            ...(this.selectedFilterStartDate.length && { startDate: getStringifiedIds(this.selectedFilterStartDate) }),
            ...(this.selectedFilterEndDate.length && { endDate: getStringifiedIds(this.selectedFilterEndDate) }),
        };
    };

    @mobx.action
    private onFetchEventsListSuccess(response: IEventsTrackerListDTO): void {
        this.eventTrackerList = response.content;
    }



    @mobx.action
    private onFetchEventsListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }

    @mobx.action
    public resetSearchQuery = (): void => {
        this.searchQuery = '';
        this.shouldRedirectToFirstPage = true;
        this.trackBySearchQuery(true);
        this.fetchEventsList();
    };

    private searchEventsCallback;
    private searchEvents = () => {
        if (this.searchQuery.trim().length >= MINIMAL_SEARCH_LENGTH) {
            this.shouldRedirectToFirstPage = true;
            this.trackBySearchQuery(false);
            this.fetchEventsList();
        }
    };

    @mobx.action
    public onChangeQuery = (query: string): void => {
        this.searchQuery = query;
        this.searchEventsCallback?.();
    };

    @mobx.action
    public onCollapseGroup = (group: IEventTrackerGroup) => {
        this.collapsedGroups = [...this.collapsedGroups, group];
    };

    @mobx.action
    public onExpandGroup = (group: IEventTrackerGroup) => {
        const { mergedDateTime } = group;

        this.collapsedGroups = this.collapsedGroups.filter(
            el => el.mergedDateTime !== mergedDateTime
        );
    };

    public fetchDeliveryTeamItems = async (query = ''): Promise<IDeliveryItemDetailsDTO[]> => {
        try {
            const response = await this.usersDAO.searchDeliveryTeamMembers({
                page: 0,
                size: 100,
                query: query,
                activeOnly: true
            });

            const formattedResponse = response.users.map(deliveryItem => ({
                ...deliveryItem,
                fullName: `${deliveryItem.firstName} ${deliveryItem.lastName}`,
                deliveryTeamMemberRole: null
            }));

            return formattedResponse;
        } catch (error) {
            if (error.response.status !== 403) {
                this.alertsService.alert({
                    message: this.errorParsingService.extractMessage(error)
                });
            }
        }
    };
    public trackEventMixPanel = (eventName: string, dataSaved: boolean): void => {
        this.trackingService.get().track(eventName,{
            'Data saved': convertBoolTypeForMixPanel(dataSaved)
        });
    };

    public trackCancelDeliveryTeamButtonMixPanel = (): void => {
        this.trackEventMixPanel(TRACKING_EVENTS.ADD_DELIVERY_TEAM, false);
    };

    @mobx.action
    public setAssignedDeliveryTeam = async (
        deliveryTeamMembers: IDeliveryItemDetailsDTO[],
        eventId: number,
        accountId: number
    ) => {
        const eventInfo = await this.getCurrentEvent(eventId, accountId);
        const deliveryTeamMembersMapper = DeliveryTeamMemberItemsMapper.create(eventInfo?.deliveryTeamMembers ?? []);
        const addedDeliveryTeamMembers = deliveryTeamMembersMapper.getAddedDeliveryTeamMembersToList(deliveryTeamMembers);

        await this.eventsDAO.updateEvent(accountId, {
            ...eventInfo,
            addedDeliveryTeamMembers: addedDeliveryTeamMembers
        });
        this.trackEventMixPanel(TRACKING_EVENTS.ADD_DELIVERY_TEAM, true);
        await this.fetchEventsList();
    };

    private getCurrentEvent = async (eventId: number, accountId: number) => {
        return await this.eventsDAO.getEventDetails({
            eventId: eventId,
            accountId: accountId
        });
    };

    @mobx.action
    public removeDeliveryItem = async (
        intl: IntlShape,
        userFullName: string,
        username: string,
        eventId: number,
        accountId: number
    ): Promise<void> => {
        try {
            let shouldUpdateEvent = false;
            let itemToRemove: IDeliveryItemDetailsDTO;
            const eventInfo = await this.getCurrentEvent(eventId, accountId);

            const updatedDeliveryMembers = eventInfo.deliveryTeamMembers.filter(item => {
                if (item.username === username) {
                    shouldUpdateEvent = true;
                    itemToRemove = item;
                }
                return item.username !== username;
            });

            if (shouldUpdateEvent) {
                await this.eventsDAO.updateEvent(accountId, {
                    ...eventInfo,
                    removedDeliveryTeamMembers: [itemToRemove]
                });

                mobx.runInAction(() => {
                    this.eventTrackerList.forEach(item => {
                        if (item.eventId === eventId) {
                            item.deliveryTeamMembers = updatedDeliveryMembers;
                        }
                    });
                });
            } else {
                const notAssignIntoEvent = intl.formatMessage({
                    id: 'oe.common.notAssignIntoEvent',
                    defaultMessage: 'is no longer assigned to this event. Please refresh the page to see the latest data.',
                    description: 'Text message'
                });
                this.alertsService.alert({
                    message: this.errorParsingService.extractMessage(`${userFullName} ${notAssignIntoEvent}`)
                });
            }

        } catch (error) {
            this.onFetchEventsListError(error);
        }
    };

    @mobx.action
    public setDeliveryMemberRole = async (
        intl: IntlShape,
        userFullName: string,
        username: string,
        eventId: number,
        accountId: number,
        role: string
    ): Promise<void> => {
        try {
            let shouldUpdateEvent = false;
            let itemToUpdate: IDeliveryItemDetailsDTO;
            const eventInfo = await this.getCurrentEvent(eventId, accountId);

            const updatedDeliveryMembers = eventInfo.deliveryTeamMembers.map(item => {
                if (item.username === username) {
                    item.deliveryTeamMemberRole = role === item.deliveryTeamMemberRole ? null : role;
                    shouldUpdateEvent = true;
                    itemToUpdate = item;
                }
                return item;
            });

            if (shouldUpdateEvent) {
                await this.eventsDAO.updateEvent(accountId, {
                    ...eventInfo,
                    updatedDeliveryTeamMembers: [itemToUpdate]
                });

                mobx.runInAction(() => {
                    this.eventTrackerList.forEach(item => {
                        if (item.eventId === eventId) {
                            item.deliveryTeamMembers = updatedDeliveryMembers;
                        }
                    });
                });
            } else {
                const notAssignIntoEvent = intl.formatMessage({
                    id: 'oe.common.notAssignIntoEvent',
                    defaultMessage: 'is no longer assigned to this event. Please refresh the page to see the latest data.',
                    description: 'Text message'
                });
                this.alertsService.alert({
                    message: this.errorParsingService.extractMessage(`${userFullName} ${notAssignIntoEvent}`)
                });
            }

        } catch (error) {
            this.onFetchEventsListError(error);
        }
    };

    public fetchDeliveryTeamList = async (query: string): Promise<{ id: string; label: string }[]> => {
        const response = await this.usersDAO.searchDeliveryTeamMembers({
            page: 0,
            size: 100,
            query: query ? query.trim() : ''
        });
        return response.users.map(deliveryItem => (
            { id: deliveryItem.username, label: `${deliveryItem.firstName} ${deliveryItem.lastName}` }
        ));
    };

    public fetchCoordinatorsList = async (query: string): Promise<{ id: string; label: string }[]> => {
        const response = await this.usersDAO.getAccountCoordinators({
            page: 0,
            size: 100,
            query: query ? query.trim() : '',
        });
        return response.users.map(deliveryItem => (
            { id: deliveryItem.username, label: `${deliveryItem.firstName} ${deliveryItem.lastName}` }
        ));
    };

    public fetchAccountsList = async (query: string): Promise<{ id: string; label: string }[]> => {
        const response = await this.accountsDAO.getUserAccounts({
            page: 0,
            size: 100,
            query: query ? query.trim() : ''
        });
        return response.content.map(account => (
            { id: account.accountId.toString(), label: account.name }
        ));
    };

    public fetchFilterViews = async () => {
        const items = await this.eventsDAO.getEventTrackerFilterViews({
            filterViewTab: EVENT_TRACKER_FILTER_VIEW_TAB_MAPPER[this.tabName]});

        mobx.runInAction(() => {
            this.filterViews = items;
        });
    };

    public deleteFilterViewApiCall = async (id: number) => {
        try {
            await this.eventsDAO.deleteEventTrackerFilterView({ userFilterViewId: id });
            this.fetchFilterViews();
        } catch (error) {
            this.showErrorModal(error);
        }
    };

    public saveFilterView = async (view: IFiltersView): Promise<boolean> => {
        try {
            await this.eventsDAO.createEventTrackerFilterView({...view, filterViewTab: EVENT_TRACKER_FILTER_VIEW_TAB_MAPPER[this.tabName] });
            this.fetchFilterViews();
            this.trackingService.get().track(TRACKING_EVENTS.SAVE_FILTERED_VIEW, {});
            return true;
        } catch (error) {
            this.showErrorModal(error);
        }
    };

    public showErrorModal = error => {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    };

    @mobx.action
    public handleChangeDeliveryTeamFilter = (deliveryTeamItems: IFilterValue[]) => {
        this.selectedFilterDeliveryTeam = deliveryTeamItems;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Delivery Team',
            isReseted: isEmptyArray(deliveryTeamItems)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeCoordinatorsFilter = (coordinatorItems: IFilterValue[]) => {
        this.selectedFilterCoordinators = coordinatorItems;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Coordinators',
            isReseted: isEmptyArray(coordinatorItems)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeAccountsFilter = (accountItems: IFilterValue[]) => {
        this.selectedFilterAccounts = accountItems;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Account',
            isReseted: isEmptyArray(accountItems)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeEventTypeFilter = (eventTypeItems: IFilterValue[]) => {
        this.selectedFilterEventTypes = eventTypeItems;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Type',
            isReseted: isEmptyArray(eventTypeItems)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeEventLinksFilter = (eventLinkItems: IFilterValue[]) => {
        this.selectedFilterEventLinks = eventLinkItems;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Links',
            isReseted: isEmptyArray(eventLinkItems)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeEventRegionsFilter = (eventRegionItems: IFilterValue[]) => {
        this.selectedFilterEventRegions = eventRegionItems;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Region',
            isReseted: isEmptyArray(eventRegionItems)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeBusinessLinesFilter = (eventBusinessLineItems: IFilterValue[]) => {
        this.selectedFilterEventBusinessLines = eventBusinessLineItems;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Business Line',
            isReseted: isEmptyArray(eventBusinessLineItems)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeDateRangeFilter = (dates: Date[]) => {
        this.selectedFilterStartDate = dates[0] && dates[1] ? [{
            id: format(dates[0],'yyyy-MM-dd'),
            label: 'startDate'
        }] : [];
        this.selectedFilterEndDate = dates[0] && dates[1] ? [{
            id: format(dates[1],'yyyy-MM-dd'),
            label: 'endDate'
        }] : [];
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Date',
            isReseted: isEmptyArray(dates)
        });
        this.fetchEventsList();
    };

    @mobx.action
    public handleChangeEventFilterColumns = (eventColumnsItems: IFilterValue[]) => {
        this.selectedFilterColumns = eventColumnsItems
            .slice()
            .sort((a, b) => {
                const aIndex = this.defaultColumns.findIndex(c => c.id === a.id);
                const bIndex = this.defaultColumns.findIndex(c => c.id === b.id);
                return aIndex - bIndex;
            });
        this.addFiltersToStorage({
            selectedFilter: 'Columns',
            isReseted: isEmptyArray(eventColumnsItems)
        });
    };

    @mobx.action
    public clearFilters = () => {
        this.selectedFilterCoordinators = [];
        this.selectedFilterDeliveryTeam = [];
        this.selectedFilterAccounts = [];
        this.selectedFilterEventTypes = [];
        this.selectedFilterEventLinks = [];
        this.selectedFilterEventRegions = [];
        this.selectedFilterEventBusinessLines = [];
        this.selectedFilterStartDate = [];
        this.selectedFilterEndDate = [];
        this.selectedFilterColumns = this.defaultColumns;
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage({
            selectedFilter: 'Clear All Filters',
            isReseted: false
        });
        this.fetchEventsList();
    };

    @mobx.action
    public resetSelectedFilterColumns = () => {
        this.selectedFilterColumns = this.defaultColumns;
    };

    @mobx.action
    public selectFilters = (filters: ISelectedFilter[], isTriggeredOnDidMount?: boolean) => {
        this.selectedFilterDeliveryTeam = filters.find(el => el.filterName === 'Delivery Team')?.filterValues || [];
        this.selectedFilterAccounts = filters.find(el => el.filterName === 'Accounts')?.filterValues || [];
        this.selectedFilterEventTypes = filters.find(el => el.filterName === 'Types')?.filterValues || [];
        this.selectedFilterEventLinks = filters.find(el => el.filterName === 'Links')?.filterValues || [];
        this.selectedFilterEventRegions = filters.find(el => el.filterName === 'Regions')?.filterValues || [];
        this.selectedFilterEventBusinessLines = filters.find(el => el.filterName === 'Business Lines')?.filterValues || [];
        this.selectedFilterStartDate = filters.find(el => el.filterName === 'Start Date')?.filterValues || [];
        this.selectedFilterEndDate = filters.find(el => el.filterName === 'End Date')?.filterValues || [];
        this.selectedFilterColumns = filters.find(el => el.filterName === 'Columns')?.filterValues || this.defaultColumns;
        this.selectedFilterCoordinators = filters.find(el => el.filterName === 'Coordinators')?.filterValues || [];
        this.paginationVM.selectPage(1);

        this.addFiltersToStorage(null, true, isTriggeredOnDidMount);
        this.fetchEventsList();
    };

    public getSelectedFilters = (): ISelectedFilter[] => {
        return [
            this.selectedFilterAccounts.length && {
                filterName: 'Accounts',
                filterValues: this.selectedFilterAccounts
            },
            this.selectedFilterEventTypes.length && {
                filterName: 'Types',
                filterValues: this.selectedFilterEventTypes
            },
            this.selectedFilterCoordinators.length && {
                filterName: 'Coordinators',
                filterValues: this.selectedFilterCoordinators
            },
            this.selectedFilterDeliveryTeam.length && {
                filterName: 'Delivery Team',
                filterValues: this.selectedFilterDeliveryTeam
            },
            this.selectedFilterEventLinks.length && {
                filterName: 'Links',
                filterValues: this.selectedFilterEventLinks
            },
            this.selectedFilterEventRegions.length && {
                filterName: 'Regions',
                filterValues: this.selectedFilterEventRegions
            },
            this.selectedFilterEventBusinessLines.length && {
                filterName: 'Business Lines',
                filterValues: this.selectedFilterEventBusinessLines
            },
            this.selectedFilterStartDate.length && {
                filterName: 'Start Date',
                filterValues: this.selectedFilterStartDate
            },
            this.selectedFilterEndDate.length && {
                filterName: 'End Date',
                filterValues: this.selectedFilterEndDate
            },
            {
                filterName: 'Columns',
                filterValues: this.selectedFilterColumns
            }
        ].filter(Boolean);
    };

    public addFiltersToStorage = (selectedFilterOptions: TSelectedFilterOptions, isSelected?: boolean, isTriggeredOnDidMount?: boolean) => {
        const selectedFilters = this.getSelectedFilters();
        localStorage.setItem(this.localStorageFiltersName, JSON.stringify(selectedFilters));
        if (isTriggeredOnDidMount || selectedFilterOptions?.selectedFilter === 'Columns') return;
        this.trackFilterEvent(selectedFilterOptions, selectedFilters, isSelected);
    };

    public loadFiltersFromStorage = () => {
        const filtersString = localStorage.getItem(this.localStorageFiltersName);

        try {
            const filters = JSON.parse(filtersString);
            this.selectFilters(filters, true);
        } catch {
            this.clearFilters();
        }
    };

    public deleteFilterView = (id: number) => {
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSureYouWishTo'
                        description='Alert message'
                        message='Are you sure you wish to '
                    />
                    <Text
                        className='alert-message--danger'
                        id='oe.eventTracker.deleteThisSavedFilteredView'
                        description='Alert message'
                        message='delete this Saved Filtered View?'
                    />
                </>
            ),
            confirmButtonProps: {
                text: 'Yes, Delete',
                modifier: 'confirm-warn',
                handler: () => this.deleteFilterViewApiCall(id),
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    private trackBySearchQuery(isReseted: boolean): void {
        const selectedFilters = this.getSelectedFilters();
        this.trackFilterEvent(
            {
                selectedFilter: 'Search',
                isReseted: isReseted
            },
            selectedFilters
        );
    }

    private trackFilterEvent(
        selectedFilterOptions: TSelectedFilterOptions | null,
        selectedFilters: ISelectedFilter[],
        isSelected?: boolean
    ): void {
        const {
            selectedFilter,
            isReseted
        } = selectedFilterOptions ?? {};
        const isFilterBySearchQuery = selectedFilter === 'Search' || this.searchQuery.trim().length >= MINIMAL_SEARCH_LENGTH;
        const selectedFiltersNames = getFiltersNames(selectedFilters, isFilterBySearchQuery);
        const selectedTab = EVENT_TRACKER_TABS_MAPPER[this.tabName];

        if (isReseted) {
            return this.trackResetFilter(selectedFilter, selectedFiltersNames);
        } else if (isSelected) {
            this.trackSelectedFilters(selectedTab, selectedFiltersNames);
        } else {
            this.trackAppliedFilter(selectedTab, selectedFilter, selectedFiltersNames);
        }
    }

    private trackResetFilter(selectedFilter: TFilterName, selectedFiltersNames: string[]): void {
        this.trackingService.get().track(
            TRACKING_EVENTS.EVENT_TRACKER_RESET_FILTER,
            {
                'Tab': EVENT_TRACKER_TABS_MAPPER[this.tabName],
                'Reset Filter': selectedFilter,
                'Additional Filters': buildSelectedFiltersList(selectedFiltersNames, selectedFilter)
            }
        );
    }

    private trackSelectedFilters(tab: string, selectedFiltersNames: string[]): void {
        this.trackingService
            .get()
            .track(TRACKING_EVENTS.SELECT_FILTERED_VIEW, {
                Tab: tab,
                'Selected Filters': buildSelectedFiltersList(selectedFiltersNames)
            })
        ;
    }

    private trackAppliedFilter(
        tab: string,
        selectedFilter: TFilterName,
        selectedFiltersNames: string[]
    ): void {
        this.trackingService
            .get()
            .track(TRACKING_EVENTS.FILTERING_BY_ANY_OF_THE_FILTERS, {
                'Tab': tab,
                'Applied Filter': selectedFilter,
                'Additional Filters': buildSelectedFiltersList(selectedFiltersNames, selectedFilter)
            })
        ;
    }
}

export { EventTrackerVM };
